import { ReactUtils } from '@festina-life/core'
import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/react'
import cx from 'classnames'
import { type FC, Fragment, type ReactNode } from 'react'

import { selectStyles } from '../../forms/select/selectStyles'
import { dropdownStyles } from '../../libs/dropdownStyles'
import { HorizontalDivider } from '../dividers/HorizontalDivider'

type Props = {
  /** The element to display could be for example text or an image */
  element: ReactNode
  /** The items to display in the dropdown */
  items: (
    | {
        element: ReactNode | FC<{ onClose: () => void }>
        onClick?: () => void
        isDisabled?: boolean
        isDivider?: never
      }
    | {
        isDivider: true
        element?: never
        onClick?: never
        isDisabled?: never
      }
  )[]
  menuClassName?: string
}

/**
 * Component for wrapping an icon or text in a button, that then has a dropdown of items
 */
export const Dropdown = ({ element, items, menuClassName }: Props) => (
  <Menu as={Fragment}>
    <MenuButton as={Fragment}>{element}</MenuButton>
    <MenuItems
      transition
      modal={false} // Deliberately disabling features like scroll locking, focus trapping, and making other elements inert.
      anchor="bottom start"
      className={cx(
        selectStyles.options,
        menuClassName,
        '[--anchor-padding:--spacing(2)]', // To ensure dropdown has padding to viewport edge
        'min-w-(--button-width)', // Ensure dropdown is at least as wide as the button
        dropdownStyles,
      )}
    >
      {items.map(
        ({ element: Element, onClick, isDisabled, isDivider }, index) =>
          isDivider ? (
            <HorizontalDivider
              // oxlint-disable-next-line react/no-array-index-key -- we don't have a unique key
              key={index}
            />
          ) : (
            // oxlint-disable-next-line react/no-array-index-key -- we don't have a unique key
            <MenuItem key={index} as={Fragment} disabled={isDisabled}>
              {({ focus, disabled, close }) => (
                <ReactUtils.ConditionalWrap
                  condition={!!onClick}
                  wrap={(children) => (
                    <Button
                      role="button"
                      disabled={disabled}
                      className={cx(
                        selectStyles.option({
                          hasFocus: focus,
                          isDisabled: disabled,
                          isSelected: false,
                        }),
                        'hover:bg-primary/10',
                      )}
                      onClick={() => {
                        onClick?.() // TODO: keyboard navigation i wonky so "clicking" with the keyboard does not work but it was even more broken before... https://festinafinance.atlassian.net/browse/LIFE-7654
                        close()
                      }}
                    >
                      {children}
                    </Button>
                  )}
                >
                  {/* oxlint-disable-next-line no-nested-ternary */}
                  {typeof Element === 'function' ? (
                    <Element onClose={close} />
                  ) : onClick ? (
                    <>{Element}</>
                  ) : (
                    <div className="px-2 py-1.5">{Element}</div>
                  )}
                </ReactUtils.ConditionalWrap>
              )}
            </MenuItem>
          ),
      )}
    </MenuItems>
  </Menu>
)
